import React, { useEffect, useState } from 'react';
import { frameworksApi } from '../../api/frameworks';
import { parseStoryFrameworkRef } from '../../api/frameworks';
import { plotDossiersApi } from '../../api/plotDossiers';
import ThinGenreMarker, { beatCiteLabel } from '../Frameworks/ThinGenreMarker';
import './FrameworkReferencePanel.css';

const STATUS_LABELS = {
  unaddressed: 'Unaddressed',
  partial: 'Partial',
  resolved: 'Resolved',
};

function groupBeats(beats) {
  const groups = [];
  (beats || []).forEach((beat) => {
    const last = groups[groups.length - 1];
    if (!last || last.act !== beat.act) {
      groups.push({ act: beat.act, beats: [beat] });
    } else {
      last.beats.push(beat);
    }
  });
  return groups;
}

function coverageReady(gapState, frameworkRef) {
  return !!(gapState && frameworkRef && gapState.framework_ref === frameworkRef);
}

function itemStatus(statuses, name) {
  const status = (statuses || {})[name];
  if (status === 'partial' || status === 'resolved' || status === 'unaddressed') {
    return status;
  }
  return 'unaddressed';
}

/**
 * Read-only Plot Focus reference. Notebook treatment on purpose —
 * this is brainstorming notes, never the Story Board.
 */
export default function FrameworkReferencePanel({
  frameworkRef,
  storyId = null,
  declined,
  gapState,
  onPick,
  onClear,
}) {
  const [dto, setDto] = useState(null);
  const [error, setError] = useState(null);
  const [storyRef, setStoryRef] = useState(null);

  // A sequence session can have no lens of its own while the book already
  // has a Framework. The header shows that Framework. This panel should too.
  useEffect(() => {
    if (frameworkRef || declined || !storyId) {
      setStoryRef(null);
      return undefined;
    }
    let cancelled = false;
    plotDossiersApi.status(storyId)
      .then((status) => {
        const body = status?.data || status;
        if (!cancelled) setStoryRef(body?.framework_ref || null);
      })
      .catch(() => {
        if (!cancelled) setStoryRef(null);
      });
    return () => { cancelled = true; };
  }, [frameworkRef, declined, storyId]);

  const activeRef = frameworkRef || storyRef;

  useEffect(() => {
    const parsed = parseStoryFrameworkRef(activeRef);
    if (!parsed) {
      setDto(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const loaded = parsed.source === 'custom'
          ? await frameworksApi.getCustom(parsed.id)
          : await frameworksApi.getLibrary(parsed.id);
        if (!cancelled) {
          setDto(loaded);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setDto(null);
          setError(err.message || 'Could not load this reference.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeRef]);

  if (!activeRef || declined) {
    return (
      <aside className="plot-ref" aria-label="Plot brainstorming reference">
        <p className="plot-ref__stamp">Brainstorming reference — not saved to your story</p>
        <div className="plot-ref__empty">
          <p>No Framework selected. Use one as a disposable lens, or keep going without it.</p>
          <button type="button" className="plot-ref__btn" onClick={onPick}>
            Choose a Framework
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="plot-ref" aria-label="Plot brainstorming reference">
      <p className="plot-ref__stamp">Brainstorming reference — not saved to your story</p>
      {error && <p className="plot-ref__error">{error}</p>}
      {dto && (
        <>
          <h2 className="plot-ref__title">{dto.framework_name}</h2>
          {dto.description && <p className="plot-ref__lede">{dto.description}</p>}
          <ThinGenreMarker show={Boolean(dto.thin_genre)} />
          {(dto.tracks || []).length > 0 && (
            <section className="plot-ref__act">
              <h3 className="plot-ref__act-title">Tracks</h3>
              <ul className="plot-ref__beats">
                {dto.tracks.map((track) => (
                  <li key={track.name} className="plot-ref__beat">
                    <strong>{track.name}</strong>
                    <p>{track.purpose}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {coverageReady(gapState, activeRef) && (
            <p className="plot-ref__coverage">
              Coverage from this conversation — Unaddressed, Partial, or Resolved.
            </p>
          )}
          {(dto.tropes || []).length > 0 && (
            <section className="plot-ref__act">
              <h3 className="plot-ref__act-title">Tropes</h3>
              <ul className="plot-ref__beats">
                {dto.tropes.map((trope) => {
                  const scored = trope.checkable && coverageReady(gapState, activeRef);
                  const status = scored
                    ? itemStatus(gapState.trope_statuses, trope.name)
                    : null;
                  return (
                    <li
                      key={trope.name}
                      className={status ? `plot-ref__beat plot-ref__beat--${status}` : 'plot-ref__beat'}
                    >
                      <strong>{trope.name}</strong>
                      {status && (
                        <span className="plot-ref__status">{STATUS_LABELS[status]}</span>
                      )}
                      {!trope.checkable && (
                        <span className="plot-ref__status">Reference only</span>
                      )}
                      <p>{trope.purpose}</p>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
          {groupBeats(dto.beats).map((group) => (
            <section key={group.act} className="plot-ref__act">
              {group.act != null && <h3 className="plot-ref__act-title">Act {group.act}</h3>}
              <ol className="plot-ref__beats">
                {group.beats.map((beat) => {
                  const status = coverageReady(gapState, activeRef)
                    ? itemStatus(gapState.statuses, beat.name)
                    : null;
                  return (
                    <li
                      key={`${group.act}-${beat.name}`}
                      className={status ? `plot-ref__beat plot-ref__beat--${status}` : 'plot-ref__beat'}
                    >
                      <strong>{beat.name}</strong>
                      <span className="plot-ref__status">{beatCiteLabel(beat)}</span>
                      {status && (
                        <span className="plot-ref__status">{STATUS_LABELS[status]}</span>
                      )}
                      <p>{beat.purpose}</p>
                    </li>
                  );
                })}
              </ol>
            </section>
          ))}
        </>
      )}
      <div className="plot-ref__actions">
        <button type="button" className="plot-ref__btn" onClick={onPick}>Change</button>
        {onClear && (
          <button type="button" className="plot-ref__btn plot-ref__btn--quiet" onClick={onClear}>
            Work without a Framework
          </button>
        )}
      </div>
    </aside>
  );
}
